import { Helmet } from 'react-helmet-async';
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./BlogReadMore3.module.css";
import Gfooter from "../Component/Gfooter";

/* ── Variants ── */
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] },
  },
});

const heroTitleVar = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};

const heroBgVar = {
  hidden: { scale: 1.14, opacity: 0 },
  show: { scale: 1, opacity: 1, transition: { duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const galleryItemVar = {
  hidden: { opacity: 0, scale: 0.94 },
  show: (i) => ({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: i * 0.07, ease: "easeOut" },
  }),
};

const lbVar = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.22 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const lbImgVar = {
  hidden: { opacity: 0, scale: 0.9 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.35, ease: "easeOut" } },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.22 } },
};

function BlogReadMore3(){
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [multiImages, setMultiImages] = useState([]);
  const [selected, setSelected] = useState(null);

  const createSlug = (text = "") =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

  
useEffect(() => {
  window.scrollTo(0, 0);

  const fetchBlog = async () => {
    try {
      // First, find the blog using its slug
      const listRes = await axios.get(
        "https://cyberathon.com/NLF/index.php/Api/Nlf_Controller/list_blog_card"
      );

      const blogs = listRes.data.data || [];

      const matchedBlog = blogs.find(
        (item) =>
          item.slug === slug ||
          createSlug(item.seo_title || item.title) === slug ||
          String(item.id) === slug
      );

      if (!matchedBlog) {
        setBlog(null);
        return;
      }

      // Fetch complete blog details using its existing database ID
      const fd = new FormData();
      fd.append("id", matchedBlog.id);

      const res = await axios.post(
        "https://cyberathon.com/NLF/index.php/Api/Nlf_Controller/get_by_id_blog_card",
        fd
      );

      if (res.data.status === true) {
        const data = res.data.data;
        setBlog(data);

        if (Array.isArray(data.multi_image)) {
          setMultiImages(data.multi_image);
        } else if (typeof data.multi_image === "string") {
          setMultiImages(data.multi_image.split(","));
        } else {
          setMultiImages([]);
        }
      }
    } catch (error) {
      console.error("Error fetching blog:", error);
    }
  };

  fetchBlog();
}, [slug]);

  /* keyboard nav */
  useEffect(() => {
    const onKey = (e) => {
      if (selected === null) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  // Update SEO metadata when product details are loaded
    
useEffect(() => {
  if (!blog) return;

  const title =
    blog.seo_title?.trim() ||
    blog.title?.trim() ||
    "NLF Solutions";

  const description =
    blog.meta_description?.trim() || "";

  document.title = `${title} | NLF Solutions`;

  const setMetaTag = (name, content) => {
    let tag = document.querySelector(
      `meta[name="${name}"]`
    );

    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", name);
      document.head.appendChild(tag);
    }

    tag.setAttribute("content", content);
  };

  setMetaTag("description", description);

  const canonical = document.querySelector(
    'link[rel="canonical"]'
  );

  if (canonical) {
    canonical.setAttribute(
      "href",
      `https://nlfs.in/blogRead/${createSlug(
        blog.seo_title || blog.title
      )}`
    );
  }
}, [blog]);


  /* Loading */
  if (!blog) {
    return (
      <div className={styles.loading}>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className={styles.loadDot}
            animate={{ y: [0, -14, 0] }}
            transition={{ repeat: Infinity, duration: 0.9, delay: i * 0.18 }}
          />
        ))}
      </div>
    );
  }

  const imgBase = "https://cyberathon.com/NLF/";
  const heroImg = blog.blog_image ? `${imgBase}${blog.blog_image}` : null;
  const fullImgs = multiImages.map((img) => `${imgBase}${img.trim()}`);

  const prev = () => setSelected((s) => (s === 0 ? fullImgs.length - 1 : s - 1));
  const next = () => setSelected((s) => (s === fullImgs.length - 1 ? 0 : s + 1));


  return (
    <>
      <Helmet>
  <title>
    {blog.seo_title || blog.title} | NLF Solutions
  </title>

  <meta
    name="description"
    content={blog.meta_description || ""}
  />

  <link
    rel="canonical"
    href={`https://nlfs.in/blogRead/${createSlug(
      blog.seo_title || blog.title
    )}`}
  />
</Helmet>
      <div className={styles.page}>

        {/* ══════════════ HERO ══════════════ */}
        <div className={styles.hero}>
          {heroImg && (
            <motion.img
              src={heroImg}
              alt={blog.title}
              className={styles.heroBg}
              variants={heroBgVar}
              initial="hidden"
              animate="show"
            />
          )}
          <div className={styles.heroVeil} />
          <div className={styles.heroSlice} />

          <div className={styles.heroContent}>
            {/* Breadcrumb */}
            <motion.div
              className={styles.heroCrumb}
              variants={fadeUp(0.3)}
              initial="hidden"
              animate="show"
            >
              <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>Home</Link>
              <span className={styles.heroCrumbSep}>›</span>
              <Link to="/blogNew2" style={{ color: "inherit", textDecoration: "none" }}>Blogs</Link>
              <span className={styles.heroCrumbSep}>›</span>
              <span className={styles.heroCrumbActive}>Article</span>
            </motion.div>

            {/* Title */}
            <motion.h1
              className={styles.heroTitle}
              variants={heroTitleVar}
              initial="hidden"
              animate="show"
            >
              {blog.title}
            </motion.h1>

            {/* Meta */}
            <motion.div
              className={styles.heroMeta}
              variants={fadeUp(0.55)}
              initial="hidden"
              animate="show"
            >
              <span className={styles.heroDate}>
                <span className={styles.heroDot} />
                {blog.day} {blog.month} {blog.year}
              </span>
            </motion.div>
          </div>
        </div>

        {/* ══════════════ BODY ══════════════ */}
        <div className={styles.body}>


          <motion.div
            className={styles.descSection}
            variants={fadeUp(0)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <div className={styles.sectionLabel}>Story</div>
            <div
              className={styles.descText}
              dangerouslySetInnerHTML={{ __html: blog.description }}
            />
          </motion.div>


          <motion.div
            className={styles.sidebar}
            variants={fadeUp(0.15)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >

            {/* <div className={styles.sideCard}>
              <div className={styles.redDivider} />
              <div className={styles.sideCardTitle}>Published</div>
              <div className={styles.sideCardBody}>
                {blog.day} {blog.month} {blog.year}
              </div>
            </div> */}
            {/* SIDEBAR OUTSIDE BODY */}
            {/* <div className={styles.sidebarFixed}> */}
            <div className={styles.sideCard}>
              <div className={styles.redDivider} />
              <div className={styles.sideCardTitle}>Published</div>
              <div className={styles.sideCardBody}>
                {blog.day} {blog.month} {blog.year}
              </div>
            </div>
            {/* </div> */}
            {/* <div className={styles.sideCard}>
              <div className={styles.redDivider} />
              <div className={styles.sideCardTitle}>About NLF</div>
              <div className={styles.sideCardBody}>
                Enhancing building envelopes with world-class facades, ceilings, furniture and roofing solutions.
              </div>
            </div> */}

            {/* Back */}
            <Link to="/blognew2" className={styles.backBtn}>
              <svg viewBox="0 0 24 24">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Blogs
            </Link>
          </motion.div>

          {/* ── GALLERY (full-width) ── */}
          {fullImgs.length > 0 && (
            <div className={styles.gallerySection}>
              <motion.div
                variants={fadeUp(0)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                <div className={styles.sectionLabel}>Gallery</div>
              </motion.div>

              <div className={styles.galleryGrid}>
                {fullImgs.slice(0, 5).map((img, i) => (
                  <motion.div
                    key={i}
                    className={styles.galleryItem}
                    custom={i}
                    variants={galleryItemVar}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    onClick={() => setSelected(i)}
                  >
                    <img src={img} alt={`gallery-${i}`} />
                    <div className={styles.galleryOverlay}>
                      <div className={styles.zoomIcon}>
                        <svg viewBox="0 0 24 24">
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══════════════ LIGHTBOX ══════════════ */}
      <AnimatePresence>
        {selected !== null && (
          <motion.div
            className={styles.lightbox}
            variants={lbVar}
            initial="hidden"
            animate="show"
            exit="exit"
            onClick={() => setSelected(null)}
          >
            <div className={styles.lbInner} onClick={(e) => e.stopPropagation()}>

              <div className={styles.lbCounter}>
                {selected + 1} &nbsp;/&nbsp; {fullImgs.length}
              </div>

              <button className={styles.lbClose} onClick={() => setSelected(null)}>✕</button>

              <button className={`${styles.lbArrow} ${styles.lbLeft}`} onClick={prev}>❮</button>

              <AnimatePresence mode="wait">
                <motion.img
                  key={selected}
                  src={fullImgs[selected]}
                  alt="expanded"
                  className={styles.lbImg}
                  variants={lbImgVar}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                />
              </AnimatePresence>

              <button className={`${styles.lbArrow} ${styles.lbRight}`} onClick={next}>❯</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Gfooter />
    </>
  );
}

export default BlogReadMore3;